import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi } from '../../lib/adminApi';
import { formatDate, fromDateTimeLocal } from '../../lib/format';

/**
 * The reporting baseline: one property-wide date the dashboard's figures count
 * from (average response, mean repair, both breach counts, photo proof — and the
 * two breakdown panels with them).
 *
 * A lens, never a rewrite. The server changes no ticket row — the queue, the
 * exports and the audit trail keep everything — which is why every action here
 * confirms first with copy saying exactly that: "Reset" reads as destructive, and
 * an operator should hesitate over the wrong fear, not this one. Clearing the date
 * brings every original figure back, and that reversibility is the proof nothing
 * was destroyed.
 *
 * Property-wide on purpose, not per-user: these figures are a shared operational
 * fact, and two managers reading "Mean repair time" over different periods is how
 * a number stops being trusted (DEBT-18 holds the personal-range idea).
 */

/** What the operator is about to do: count from `next`, or null for everything. */
type Pending = { next: string | null };

const BUTTON =
  'min-h-[48px] rounded-lg border border-slate-700 bg-slate-900 px-4 text-sm font-medium text-slate-100';

export function AnalyticsBaselinePanel() {
  const { t } = useTranslation();
  const [baseline, setBaseline] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.superAdmin.analyticsBaseline
      .get()
      .then((view) => {
        setBaseline(view.analyticsBaselineAt);
        setLoaded(true);
      })
      .catch((cause: Error) => setError(cause.message));
  }, []);

  async function apply(next: string | null) {
    setStatus('saving');
    setError(null);
    try {
      const view = await adminApi.superAdmin.analyticsBaseline.set(next);
      setBaseline(view.analyticsBaselineAt);
      setPending(null);
      setStatus('saved');
    } catch (cause) {
      setStatus('idle');
      setError((cause as Error).message);
    }
  }

  function propose(next: string | null) {
    setStatus('idle');
    setPending({ next });
  }

  if (!loaded) {
    return error ? (
      <p role="alert" className="text-sm text-red-400">
        {error}
      </p>
    ) : (
      <p className="text-sm text-slate-400">{t('admin.loading')}</p>
    );
  }

  return (
    <div className="max-w-xl space-y-4">
      <div>
        <h2 className="text-base font-semibold text-slate-100">{t('superAdmin.baseline.title')}</h2>
        <p className="mt-1 text-sm text-slate-400">{t('superAdmin.baseline.intro')}</p>
        <p className="mt-2 text-sm text-slate-200">
          {baseline
            ? t('superAdmin.baseline.countingFrom', { date: formatDate(baseline) })
            : t('superAdmin.baseline.countingAll')}
        </p>
      </div>

      {pending ? (
        <ConfirmBox pending={pending} saving={status === 'saving'} onConfirm={() => void apply(pending.next)} onCancel={() => setPending(null)} />
      ) : (
        <BaselineActions hasBaseline={baseline !== null} onPropose={propose} />
      )}

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}
      {status === 'saved' && (
        <p role="status" className="text-sm text-emerald-400">
          {t('superAdmin.baseline.saved')}
        </p>
      )}
    </div>
  );
}

function BaselineActions({
  hasBaseline,
  onPropose,
}: {
  hasBaseline: boolean;
  onPropose: (next: string | null) => void;
}) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState('');

  return (
    <div className="space-y-3">
      <button type="button" onClick={() => onPropose(new Date().toISOString())} className={BUTTON}>
        {t('superAdmin.baseline.resetToday')}
      </button>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-400">
          {t('superAdmin.baseline.pickLabel')}
          <input
            type="date"
            value={picked}
            onChange={(event) => setPicked(event.target.value)}
            className="min-h-[48px] rounded-lg border border-slate-700 bg-slate-900 px-2 text-sm text-slate-100"
          />
        </label>
        <button
          type="button"
          // Midnight in property time on the chosen day — the floor's midnight,
          // not the browser's, or a manager abroad would draw a different line.
          onClick={() => onPropose(fromDateTimeLocal(`${picked}T00:00`))}
          disabled={!picked}
          className={`${BUTTON} disabled:opacity-50`}
        >
          {t('superAdmin.baseline.apply')}
        </button>
      </div>

      {hasBaseline && (
        <button type="button" onClick={() => onPropose(null)} className={BUTTON}>
          {t('superAdmin.baseline.clear')}
        </button>
      )}
    </div>
  );
}

function ConfirmBox({
  pending,
  saving,
  onConfirm,
  onCancel,
}: {
  pending: Pending;
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3 rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
      <p className="text-sm font-medium text-slate-100">
        {pending.next
          ? t('superAdmin.baseline.confirmSet', { date: formatDate(pending.next) })
          : t('superAdmin.baseline.confirmClear')}
      </p>
      {/* The load-bearing sentence: "Reset" reads as destructive, and it is not. */}
      <p className="text-sm text-slate-300">{t('superAdmin.baseline.confirmBody')}</p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onConfirm}
          disabled={saving}
          className="min-h-[48px] rounded-lg bg-brookfields-gold px-6 text-sm font-semibold text-slate-950 disabled:opacity-50"
        >
          {t('superAdmin.baseline.confirm')}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className={BUTTON}>
          {t('superAdmin.baseline.cancel')}
        </button>
      </div>
    </div>
  );
}
